import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import {
  Close,
  Info,
  Menu as MenuIcon,
  NotificationsNone,
  Person,
} from "@mui/icons-material"
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Snackbar,
  Stack,
  styled,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import MuiAppBar from "@mui/material/AppBar"
import Toolbar from "@mui/material/Toolbar"
import { FC, useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink, useNavigate } from "react-router"
import { selectProfile } from "src/features/profile/profileSlice"
import mblsLogo from "../../assets/mobilus-logo.png"
import { useGetNotificationsQuery } from "../apis/appApi"

import { appConstants } from "../appConstants"
import { useAppDispatch, useAppSelector } from "../hooks/hooks"
import { dismissToast, WsState } from "../slices/wsSlice"
import { getAvatarColor, getInitials } from "./customers/utils"
import { DebugDialog } from "./DebugDialog"
import { InfoDialog } from "./InfoDialog"
import { MainAppbarAvatarMenu } from "./MainAppbarAvatarMenu"
import { MainAppbarNotifications } from "./MainAppbarNotifications"
import { NavDrawerList } from "./nav/NavDrawerList"
import { NavGroupLinks } from "./nav/NavGroupLinks"

const STORAGE_KEY = "debug:appbarColor"

/** Returns true if the hex color is perceptually dark (luminance < 0.5) */
const isDark = (hex: string): boolean => {
  const c = hex.replace("#", "")
  const r = parseInt(c.substring(0, 2), 16) / 255
  const g = parseInt(c.substring(2, 4), 16) / 255
  const b = parseInt(c.substring(4, 6), 16) / 255
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b
  return luminance < 0.5
}

const wsStatusColorMap: Record<WsState["connectionStatus"], string> = {
  connected: "#4caf50",
  connecting: "#ff9800",
  error: "#f44336",
  disconnected: "#bdbdbd",
}

const StyledAppBar = styled(MuiAppBar)(() => ({
  backgroundColor: "#fff",
  height: appConstants.mainAppBarHeight,
}))

export const MainAppbar: FC = () => {
  const theme = useTheme()
  // Below lg the 8 nav groups no longer fit the bar — use the drawer
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"))
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const profile = useAppSelector(selectProfile)
  const connectionStatus = useAppSelector((state) => state.ws.connectionStatus)
  const toast = useAppSelector((state) => state.ws.toast)
  const navigate = useNavigate()

  const { data: notifData } = useGetNotificationsQuery()
  const notifications = (notifData?.data ?? []).filter(Boolean)
  const unreadCount = notifications.length

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [infoOpen, setInfoOpen] = useState(false)
  const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null)
  const [navDrawerOpen, setNavDrawerOpen] = useState(false)

  // Debug dialog & appbar color
  const [debugOpen, setDebugOpen] = useState(false)
  const [appbarColor, setAppbarColor] = useState(
    () => localStorage.getItem(STORAGE_KEY) ?? "#ffffff",
  )
  const dark = isDark(appbarColor)

  const handleColorChange = useCallback((hex: string) => {
    setAppbarColor(hex)
    localStorage.setItem(STORAGE_KEY, hex)
  }, [])

  // 6-rapid-click detector
  const clickTimestamps = useRef<number[]>([])
  useEffect(() => {
    const handler = () => {
      const now = Date.now()
      clickTimestamps.current.push(now)
      // Keep only the last 6
      if (clickTimestamps.current.length > 6) {
        clickTimestamps.current = clickTimestamps.current.slice(-6)
      }
      if (clickTimestamps.current.length === 6) {
        const first = clickTimestamps.current[0]
        if (now - first <= 3000) {
          clickTimestamps.current = []
          setDebugOpen(true)
        }
      }
    }
    document.addEventListener("click", handler)
    return () => document.removeEventListener("click", handler)
  }, [])

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const fullName = [profile?.user?.firstName, profile?.user?.lastName]
    .filter(Boolean)
    .join(" ")
  const initials = getInitials(profile?.user?.firstName, profile?.user?.lastName)
  const avatarColor = getAvatarColor(fullName || "User")

  return (
    <StyledAppBar
      {...uiSurface("UI-DLV2-005")}
      color={"textPrimary" as any}
      position="fixed"
      variant={"outlined"}
      elevation={0}
      sx={{ backgroundColor: appbarColor }}
    >
      <Toolbar sx={{ gap: 1 }}>
        {/* Hamburger menu — mobile only */}
        {isMobile && (
          <IconButton
            data-testid="nav-hamburger-btn"
            edge="start"
            onClick={() => setNavDrawerOpen(true)}
            sx={dark ? { color: "#fff" } : undefined}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Logo */}
        <RouterLink to="/" style={{ display: "flex", cursor: "pointer" }}>
          <Box
            component="img"
            sx={{ height: 32, width: 120, mr: isMobile ? 0 : 2 }}
            alt="Mobilus"
            src={mblsLogo}
          />
        </RouterLink>

        {/* Navigation links — desktop only */}
        {!isMobile && <NavGroupLinks dark={dark} />}

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {!isMobile && (
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        )}

        {/* Right section: notifications, info, avatar */}
        <Stack direction="row" spacing={isMobile ? 0.5 : 1} alignItems="center">
          <IconButton
            size="small"
            data-testid="nav-notifications-btn"
            onClick={(e) => setNotifAnchorEl(e.currentTarget)}
            sx={dark ? { color: "#fff" } : undefined}
          >
            <Badge
              badgeContent={unreadCount}
              color="error"
              max={99}
              invisible={unreadCount === 0}
            >
              <NotificationsNone fontSize="small" />
            </Badge>
          </IconButton>

          <IconButton
            size="small"
            onClick={() => setInfoOpen(true)}
            sx={dark ? { color: "#fff" } : undefined}
          >
            <Info fontSize="small" />
          </IconButton>

          {/* F122: Provider code display */}
          {profile?.provider?.code && (
            <Chip
              label={profile.provider.code}
              size="small"
              variant="outlined"
              sx={{
                fontFamily: "monospace",
                fontWeight: 600,
                fontSize: 12,
                color: dark ? "white" : "text.secondary",
                borderColor: dark ? "rgba(255,255,255,0.3)" : "divider",
              }}
            />
          )}

          <Tooltip title={t(`websocket.${connectionStatus}`)}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              variant="dot"
              sx={{
                "& .MuiBadge-badge": {
                  backgroundColor: wsStatusColorMap[connectionStatus],
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  border: `2px solid ${appbarColor}`,
                },
              }}
            >
              <Avatar
                alt="User"
                data-testid="nav-avatar-btn"
                sx={{
                  bgcolor: profile ? avatarColor : "primary.main",
                  cursor: "pointer",
                  width: 34,
                  height: 34,
                  ml: 0.5,
                  fontSize: 14,
                  fontWeight: 600,
                }}
                onClick={handleAvatarClick}
              >
                {profile ? initials : <Person sx={{ fontSize: 20 }} />}
              </Avatar>
            </Badge>
          </Tooltip>
        </Stack>

        <MainAppbarAvatarMenu
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          fullName={fullName}
        />

        <MainAppbarNotifications
          anchorEl={notifAnchorEl}
          onClose={() => setNotifAnchorEl(null)}
          notifications={notifications}
        />
      </Toolbar>

      {/* Mobile nav drawer */}
      <Drawer
        anchor="left"
        open={navDrawerOpen}
        onClose={() => setNavDrawerOpen(false)}
        slotProps={{ paper: { ...uiPaper("UI-DLV2-007"), sx: { width: 280 } } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
          }}
        >
          <Box
            component="img"
            sx={{ height: 28, width: 105 }}
            alt="Mobilus"
            src={mblsLogo}
          />
          <IconButton size="small" onClick={() => setNavDrawerOpen(false)}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Divider />
        <NavDrawerList onNavigate={() => setNavDrawerOpen(false)} />
      </Drawer>

      <InfoDialog open={infoOpen} onClose={() => setInfoOpen(false)} />
      <DebugDialog
        open={debugOpen}
        onClose={() => setDebugOpen(false)}
        appbarColor={appbarColor}
        onColorChange={handleColorChange}
      />

      {/* Notification toast from MQTT */}
      <Snackbar
        open={toast?.open ?? false}
        autoHideDuration={5000}
        onClose={() => dispatch(dismissToast())}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 7 }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => dispatch(dismissToast())}
          sx={{ width: "100%", cursor: toast?.orderId ? "pointer" : undefined }}
          onClick={() => {
            if (toast?.orderId) {
              dispatch(dismissToast())
              navigate(`/orders/${toast.orderId}`)
            }
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            {toast?.title}
          </Typography>
          <Typography variant="body2">{toast?.body}</Typography>
        </Alert>
      </Snackbar>
    </StyledAppBar>
  )
}
